import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSentEmails,
  openArchivedPdf,
  type DeliveryStatus,
  type ReeksEntry,
  type SentEmail,
} from '../../lib/offerte-pdf';
import { formatDateTime, formatRelativeTime } from '../../lib/format';

interface VerzondenOffertesSectionProps {
  leadId: string;
  refreshKey: number;
  /** Drawer = compactere weergave (max 5 entries, geen details-disclosure default open). */
  variant?: 'page' | 'drawer';
}

const PENDING_STATUSES = new Set<DeliveryStatus>(['sent', 'delivery_delayed']);

const STATUS_PILL: Record<DeliveryStatus | 'unknown', { label: string; className: string }> = {
  sent: { label: 'Verstuurd', className: 'bg-slate-100 text-slate-700' },
  delivery_delayed: { label: 'Vertraagd', className: 'bg-amber-100 text-amber-800' },
  delivered: { label: 'Bezorgd', className: 'bg-emerald-100 text-emerald-700' },
  opened: { label: 'Geopend', className: 'bg-indigo-100 text-indigo-700' },
  clicked: { label: 'Aangeklikt', className: 'bg-indigo-100 text-indigo-700' },
  bounced: { label: 'Bounced', className: 'bg-red-100 text-red-700' },
  complained: { label: 'Spam', className: 'bg-orange-100 text-orange-700' },
  failed: { label: 'Mislukt', className: 'bg-red-100 text-red-700' },
  unknown: { label: 'Onbekend', className: 'bg-slate-100 text-slate-500' },
};

const EVENT_LABEL: Record<string, string> = {
  'email.sent': 'Verstuurd',
  'email.delivered': 'Bezorgd',
  'email.delivery_delayed': 'Aflevering vertraagd',
  'email.opened': 'Geopend',
  'email.clicked': 'Link aangeklikt',
  'email.bounced': 'Bounced',
  'email.complained': 'Spamklacht',
  'email.failed': 'Mislukt',
};

function statusPill(entry: SentEmail) {
  if (entry.status === 'error') {
    return STATUS_PILL.failed;
  }
  if (entry.status === 'pending') {
    return STATUS_PILL.sent;
  }
  const ds = entry.deliveryStatus;
  if (ds && ds in STATUS_PILL) {
    return STATUS_PILL[ds];
  }
  return STATUS_PILL.unknown;
}

function recipientsLabel(entry: SentEmail): string {
  const all = [...entry.to, ...entry.cc];
  if (all.length === 0) return '(geen ontvanger)';
  const first = all[0] ?? '';
  if (all.length === 1) return first;
  return `${first}, +${all.length - 1} ander${all.length - 1 === 1 ? '' : 'en'}`;
}

const REEKS_TYPE_LABEL: Record<string, string> = {
  Q01: 'Offerte-mail',
  SMS02: 'SMS-herinnering (dag 2)',
  FU01: 'Opvolgmail 1 (dag 5)',
  FU02: 'Opvolgmail 2 (dag 10)',
  FU03: 'Opvolgmail 3 (dag 16)',
};

const REEKS_STATUS_PILL: Record<string, { label: string; className: string }> = {
  sent: { label: 'Verstuurd', className: 'bg-emerald-100 text-emerald-700' },
  failed: { label: 'Mislukt', className: 'bg-red-100 text-red-700' },
  suppressed: { label: 'Onderdrukt', className: 'bg-slate-100 text-slate-500' },
  skipped: { label: 'Overgeslagen', className: 'bg-slate-100 text-slate-500' },
};

/** Eén touch uit de automatische offerte-reeks (Q01/SMS02/FU) met inhoud. */
function ReeksRow({ entry }: { entry: ReeksEntry }) {
  const [expanded, setExpanded] = useState(false);
  const pill =
    REEKS_STATUS_PILL[entry.status] ?? { label: entry.status, className: 'bg-slate-100 text-slate-500' };
  const isSms = entry.mailType.startsWith('SMS');
  return (
    <div className="px-4 py-3 hover:bg-slate-50">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-700">
              Automatisch · {isSms ? 'SMS' : 'E-mail'}
            </span>
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${pill.className}`}
              title={entry.error ?? entry.status}
            >
              {pill.label}
            </span>
            <span className="text-xs text-slate-500" title={formatDateTime(entry.sentAt)}>
              {formatRelativeTime(entry.sentAt)}
            </span>
          </div>
          <p className="text-sm text-slate-900 mt-1 truncate">
            {REEKS_TYPE_LABEL[entry.mailType] ?? entry.mailType}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            Aan: {entry.to || '(onbekend)'}
          </p>
          {entry.status === 'failed' && entry.error && (
            <p className="text-xs text-red-600 mt-1 line-clamp-2">{entry.error}</p>
          )}
          {entry.status === 'suppressed' && entry.error && (
            <p className="text-xs text-slate-400 mt-1">Reden: {entry.error}</p>
          )}
        </div>
        {entry.body && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700 flex-shrink-0"
            aria-expanded={expanded}
          >
            {expanded ? 'Minder' : 'Inhoud'}
          </button>
        )}
      </div>
      {expanded && entry.body && (
        <pre className="mt-3 ml-1 border-l-2 border-violet-200 pl-3 text-xs text-slate-600 whitespace-pre-wrap font-sans max-h-72 overflow-y-auto">
          {entry.body}
        </pre>
      )}
    </div>
  );
}

function SentEmailRow({ entry }: { entry: SentEmail }) {
  const pill = statusPill(entry);
  const [expanded, setExpanded] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState<string | null>(null);

  const handleOpenPdf = useCallback(async () => {
    if (!entry.contentVersionId) return;
    setPdfBusy(true);
    setPdfErr(null);
    try {
      await openArchivedPdf(entry.contentVersionId);
    } catch (err) {
      setPdfErr(err instanceof Error ? err.message : 'PDF openen mislukt');
    } finally {
      setPdfBusy(false);
    }
  }, [entry.contentVersionId]);

  return (
    <div className="px-4 py-3 hover:bg-slate-50">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${pill.className}`}
              title={
                entry.status === 'error'
                  ? entry.errorMessage ?? 'Versturen mislukt'
                  : entry.deliveryStatus ?? entry.status
              }
            >
              {pill.label}
            </span>
            <span
              className="text-xs text-slate-500"
              title={formatDateTime(entry.createdAt)}
            >
              {formatRelativeTime(entry.createdAt)}
            </span>
          </div>
          <p className="text-sm text-slate-900 mt-1 truncate">
            {entry.subject || '(geen onderwerp)'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">
            Aan: {recipientsLabel(entry)} · door {entry.actorEmail}
          </p>
          {entry.status === 'error' && entry.errorMessage && (
            <p className="text-xs text-red-600 mt-1">{entry.errorMessage}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {entry.contentVersionId && (
            <button
              type="button"
              onClick={handleOpenPdf}
              disabled={pdfBusy}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-white disabled:opacity-50"
            >
              {pdfBusy ? 'Bezig...' : 'PDF tonen'}
            </button>
          )}
          {(entry.deliveryEvents.length > 0 || entry.resendId) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs px-2 py-1 text-slate-500 hover:text-slate-700"
              aria-expanded={expanded}
            >
              {expanded ? 'Minder' : 'Details'}
            </button>
          )}
        </div>
      </div>
      {pdfErr && <p className="text-xs text-red-600 mt-2">{pdfErr}</p>}
      {expanded && (
        <div className="mt-3 ml-1 border-l-2 border-slate-200 pl-3 space-y-1 text-xs text-slate-600">
          {entry.deliveryEvents.length === 0 ? (
            <p className="italic text-slate-400">Nog geen Resend-events ontvangen.</p>
          ) : (
            entry.deliveryEvents.map((ev, idx) => (
              <div key={`${ev.type}-${ev.at}-${idx}`} className="flex justify-between gap-3">
                <span>{EVENT_LABEL[ev.type] ?? ev.type}</span>
                <span className="text-slate-400">{formatDateTime(ev.at)}</span>
              </div>
            ))
          )}
          {entry.resendId && (
            <p className="text-slate-400 pt-1 border-t border-slate-100 mt-2">
              Resend-id: <code className="text-[11px]">{entry.resendId}</code>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function VerzondenOffertesSection({
  leadId,
  refreshKey,
  variant = 'page',
}: VerzondenOffertesSectionProps) {
  const [entries, setEntries] = useState<SentEmail[]>([]);
  const [reeks, setReeks] = useState<ReeksEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const result = await fetchSentEmails(leadId);
      if (!mountedRef.current) return;
      setEntries(result.entries);
      setReeks(result.reeks);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Ophalen mislukt');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    void load();
    return () => {
      mountedRef.current = false;
    };
  }, [load, refreshKey]);

  // Polling: re-fetch elke 30s zolang er pending-status entries zijn.
  useEffect(() => {
    const hasPending = entries.some((e) => {
      if (e.status === 'pending') return true;
      if (e.status === 'error') return false;
      const ds = e.deliveryStatus;
      return !ds || PENDING_STATUSES.has(ds);
    });
    if (!hasPending) return;
    const interval = setInterval(() => {
      void load();
    }, 30_000);
    return () => clearInterval(interval);
  }, [entries, load]);

  const isDrawer = variant === 'drawer';
  const cap = isDrawer ? 3 : 10;
  // Eén chronologische tijdlijn: handmatige PDF-mails + automatische reeks
  const combined: Array<
    | { kind: 'manual'; at: string; entry: SentEmail }
    | { kind: 'reeks'; at: string; entry: ReeksEntry }
  > = [
    ...entries.map((e) => ({ kind: 'manual' as const, at: e.createdAt, entry: e })),
    ...reeks.map((r) => ({ kind: 'reeks' as const, at: r.sentAt, entry: r })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));
  const visibleItems = showAll ? combined : combined.slice(0, cap);
  const hasMore = combined.length > cap && !showAll;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">
          Communicatie {combined.length > 0 && `(${combined.length})`}
        </h2>
        <button
          type="button"
          onClick={() => void load()}
          className="text-xs text-slate-400 hover:text-slate-600"
          title="Vernieuwen"
          aria-label="Vernieuwen"
        >
          ↻
        </button>
      </div>
      {loading && combined.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400 text-center">Laden...</p>
      ) : error ? (
        <p className="px-4 py-6 text-sm text-red-600 text-center">{error}</p>
      ) : combined.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-400 text-center">
          Nog geen communicatie verstuurd.
        </p>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {visibleItems.map((item) =>
              item.kind === 'manual' ? (
                <SentEmailRow key={`m-${item.entry.id}`} entry={item.entry} />
              ) : (
                <ReeksRow key={`r-${item.entry.id}`} entry={item.entry} />
              )
            )}
          </div>
          {hasMore && (
            <div className="px-4 py-2 border-t border-slate-100 text-center">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                Toon alle {combined.length}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
